/**
 * Detección de imagen negra en el dispositivo, espejo del criterio del servidor.
 *
 * El servidor decide con `image_analyzer.py:34-39` de la Lambda:
 *
 *     dark_pixel_ratio = (img_array.mean(axis=2) < threshold * 255).mean()
 *     return dark_pixel_ratio > 0.90
 *
 * o sea: media aritmética de R, G y B por píxel; el píxel es oscuro si esa media queda bajo
 * `BLACK_IMAGE_THRESHOLD * 255`; y la imagen es negra si **más** del 90 % de los píxeles lo son.
 *
 * La regla que gobierna este archivo, y que pesa más que la fidelidad: **el cliente nunca puede ser
 * más estricto que el servidor.** La app tiene builds nativos instalados, así que un rechazo de más
 * queda congelado en el binario y no se corrige a la velocidad del servidor: el usuario se queda sin
 * poder subir una foto que el servidor habría aceptado, y nadie puede hacer nada. Al revés es
 * tolerable — una foto que el cliente deja pasar la marca el servidor, que es lo que pasa hoy.
 *
 * Dos consecuencias de esa regla, y las dos son contraintuitivas:
 *
 * 1. **No se reduce la imagen antes de medir.** Promediar a una grilla chica NO es conservador:
 *    en una imagen uniformemente tenue apenas sobre el umbral —ruido de valor 5, que es la forma
 *    real de una foto con el lente tapado— cada bloque promedia a un valor bajo el umbral y el
 *    cliente la marca negra cuando el servidor no lo hace. Verificado ejecutando las dos fórmulas.
 *    Si alguna vez hace falta reducir por costo, hay que tomar el **máximo** del bloque y no la
 *    media: un bloque es oscuro solo si TODOS sus píxeles lo son, y eso sí garantiza que el ratio
 *    del cliente nunca supere al del servidor.
 * 2. **Se descuenta un margen del umbral.** El decodificador JPEG del navegador y el de PIL pueden
 *    diferir en ±1 por píxel, y con un umbral de 2,55 un píxel en 2 o 3 cambia de lado. Descontar el
 *    margen hace que el cliente cuente menos píxeles oscuros, o sea que quede más laxo.
 */

/** Espeja `BLACK_IMAGE_THRESHOLD` de `config.py` de la Lambda. */
export const BLACK_IMAGE_THRESHOLD = 0.01;

/** Espeja el `> 0.90` de `_is_black_image`. */
export const BLACK_PIXEL_RATIO = 0.9;

/** Cubre la discrepancia de ±1 entre decodificadores. Ver el punto 2 de arriba. */
export const DECODER_MARGIN = 1;

export interface RgbaPixels {
  data: Uint8ClampedArray | number[];
  width: number;
  height: number;
}

/**
 * Fracción de píxeles cuya media RGB queda bajo el umbral, con el margen ya descontado.
 * Recorre el buffer RGBA de a 4; el canal alfa no participa, igual que el `convert("RGB")` del
 * servidor, y el llamador es responsable de haber pintado el fondo (ver `drawForAnalysis`).
 */
export function darkPixelRatio(
  pixels: RgbaPixels,
  threshold: number = BLACK_IMAGE_THRESHOLD,
): number {
  const { data } = pixels;
  const total = pixels.width * pixels.height;
  if (total <= 0) {
    return 0;
  }
  const cutoff = threshold * 255 - DECODER_MARGIN;
  let dark = 0;
  for (let i = 0; i < total; i++) {
    const p = i * 4;
    if ((data[p] + data[p + 1] + data[p + 2]) / 3 < cutoff) {
      dark++;
    }
  }
  return dark / total;
}

export function isBlackImage(
  pixels: RgbaPixels,
  threshold: number = BLACK_IMAGE_THRESHOLD,
): boolean {
  return darkPixelRatio(pixels, threshold) > BLACK_PIXEL_RATIO;
}
